import React, { useMemo, useState } from "react";

import {
  Box,
  Grid,
  MenuItem,
  Switch,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  ArrayInput,
  Datagrid,
  FormDataConsumer,
  Labeled,
  minValue,
  number,
  NumberField,
  NumberInput,
  required,
  SelectField,
  SelectInput,
  SimpleFormIterator,
  TextField,
  TextInput,
} from "react-admin";

import {
  getCommodityUnits,
  getRailEquipmentTypes,
  getTruckEquipmentTypes,
} from "../../../utils/loadTypeUtils";
import { ARRCarTypeChoices } from "../../../utils/constants";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";

const validateRequired = [required()];
const validateNumber = [number(), minValue(0)];

export const LoadTypesPanel = ({
  commodities,
  setCommodities,
  disabled,
  ...props
}) => {
  if (disabled)
    return (
      <LoadTypesShow
        {...props}
        commodities={commodities}
        setCommodities={setCommodities}
        disabled={disabled}
      />
    );
  return (
    <LoadTypesEdit
      {...props}
      commodities={commodities}
      setCommodities={setCommodities}
      disabled={disabled}
    />
  );
};

const LoadTypesEdit = ({ commodities, setCommodities, disabled }) => {
  const [precountUpdatingById, setPrecountUpdatingById] = useState({});

  const setPrecountUpdating = (commodityId, isUpdating) => {
    setPrecountUpdatingById((prev) => ({
      ...prev,
      [commodityId]: isUpdating,
    }));
  };

  const isPrecountUpdating = (commodityId) => !!precountUpdatingById[commodityId];
  const handleCommodityChange = (evt, selectedItm) => {
    debugger;
    selectedItm.active = evt.target.checked ? "true" : "false";
    setCommodities(evt, selectedItm);
  };

  const handleUnitChange = (evt, selectedItm) => {
    if (!disabled && evt.target.value) {
      debugger;
      selectedItm.units = evt.target.value;
      setCommodities(evt, selectedItm);
    }
  };

  const handlePrecountChange = async (evt, selectedItm) => {
    debugger;
    const commodityId = selectedItm.id;
    const nextItem = {
      ...selectedItm,
      showCounter: evt.target.checked,
    };
    setPrecountUpdating(commodityId, true);
    try {
      await setCommodities(evt, nextItem);
    } finally {
      setPrecountUpdating(commodityId, false);
    }
  };

  return (
    <Box mt={2} mb={4}>
      <Grid container spacing={2} justifyContent="space-between">
        {commodities
          ?.filter((itm) => itm.commodity.name !== "Other")
          .map((itm) => (
            <Grid item container xs={12} md={6} spacing={3} key={itm.id} justifyContent="space-between" alignItems="center">
              {/* Column 1: Load type active switch + name */}
              <Grid item xs={12} md={4}>
                <Box display="flex" alignItems="center">
                  <Switch
                    id={"load-type-" + itm.commodity?.name?.toLowerCase()}
                    name={itm.commodity?.name}
                    onChange={(evt) => handleCommodityChange(evt, itm)}
                    color="primary"
                    disabled={disabled || itm.commodity?.name === "Other"}
                    checked={itm.active === "true"}
                  />
                  <Typography component="span">
                    {itm.commodity?.name}
                  </Typography>
                </Box>
              </Grid>

              {/* Column 2: Pre-count switch (center column) */}
              <Grid item xs={12} md={2}>
                {itm.commodity?.enableCounter && (
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Show Pre-count on Tally Sheet">
                      <Switch
                        name={`precount_${itm.id}`}
                        color="primary"
                        size="small"
                        checked={!!itm.showCounter}
                        onChange={(evt) => handlePrecountChange(evt, itm)}
                        disabled={
                          disabled ||
                          itm.active !== "true" ||
                          isPrecountUpdating(itm.id)
                        }
                      />
                    </Tooltip>
                    <Typography variant="body2" component="span">
                      Counter
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Column 3: Default UOM selector */}
              <Grid item xs={12} md={5}>
                {itm.commodity?.name !== "Other" && (
                  <>
                    <MuiTextField
                      select
                      name={`units_${itm.id}`}
                      label="Default UOM"
                      size="small"
                      variant="outlined"
                      required
                      fullWidth
                      disabled={disabled || itm.active !== "true"}
                      value={itm.units}
                      onChange={(evt) => handleUnitChange(evt, itm)}
                    >
                      {getCommodityUnits(itm.commodity).map((unit) => (
                        <MenuItem
                          key={unit.id}
                          value={unit.id}
                        >{`${unit.name}: ${unit.description}`}</MenuItem>
                      ))}
                    </MuiTextField>
                    <SmartTipIcon label={itm.commodity?.name} />
                  </>
                )}
              </Grid>
            </Grid>
          ))}
      </Grid>

      {/* Equipment Types */}
      <Box mt={4}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <Typography variant="overline">
              RAIL EQUIPMENT TYPES
              <SmartTipIcon
                label="rail-equipment-types"
                alignRight={false}
                mt={20}
              />
            </Typography>
            <ArrayInput
              source="railEquipmentTypes.items"
              label={false}
              fullWidth
              style={{ marginTop: -10 }}
            >
              <SimpleFormIterator disabled={disabled}>
                <FormDataConsumer>
                  {({ getSource }) => (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={5}>
                        <TextInput
                          source={getSource("name")}
                          label="Description"
                          variant="outlined"
                          fullWidth
                          disabled={disabled}
                          helperText={false}
                          validate={validateRequired}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SelectInput
                          source={getSource("arrTypeCode")}
                          label="AAR Type Code"
                          variant="outlined"
                          fullWidth
                          disabled={disabled}
                          helperText={false}
                          choices={ARRCarTypeChoices}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <NumberInput
                          source={getSource("length")}
                          label="Length"
                          variant="outlined"
                          fullWidth
                          min="0"
                          validate={validateNumber}
                          disabled={disabled}
                          helperText={false}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="overline">
              TRUCK EQUIPMENT TYPES
              <SmartTipIcon
                label="truck-equipment-types"
                alignRight={false}
                mt={20}
              />
            </Typography>
            <ArrayInput
              source="truckEquipmentTypes.items"
              label={false}
              fullWidth
              style={{ marginTop: -15 }}
            >
              <SimpleFormIterator disabled={disabled}>
                <FormDataConsumer>
                  {({ getSource }) => (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={7}>
                        <TextInput
                          source={getSource("name")}
                          label="Description"
                          variant="outlined"
                          fullWidth
                          disabled={disabled}
                          helperText={false}
                          validate={validateRequired}
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <NumberInput
                          source={getSource("length")}
                          label="Length"
                          variant="outlined"
                          fullWidth
                          min="0"
                          validate={validateNumber}
                          disabled={disabled}
                          helperText={false}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const LoadTypesShow = ({ record, commodities, disabled }) => {
  const sortedRecord = useMemo(
    () => ({
      railEquipmentTypes: { items: getRailEquipmentTypes(record) },
      truckEquipmentTypes: { items: getTruckEquipmentTypes(record) },
    }),
    [record],
  );

  return (
    <Box mt={2} mb={4}>
      <Grid container spacing={2}>
        {commodities
          ?.filter((itm) => itm.commodity.name !== "Other")
          .map((itm) => (
            <Grid item container xs={12} md={6} spacing={1} key={itm.id}>
              {/* Column 1: Load type active switch + name */}
              <Grid item xs={12} md={5}>
                <Box display="flex" alignItems="center">
                  <Switch
                    id={"load-type-" + itm.commodity?.name?.toLowerCase()}
                    name={itm.commodity?.name}
                    color="primary"
                    disabled={disabled || itm.commodity?.name === "Other"}
                    checked={itm.active === "true"}
                  />
                  <Typography component="span">
                    {itm.commodity?.name}
                  </Typography>
                </Box>
              </Grid>

              {/* Column 2: Pre-count switch (center column) */}
              <Grid item xs={12} md={2}>
                {itm.commodity?.enableCounter && (
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Show Pre-count on Tally Sheet">
                      <Switch
                        name={`precount_${itm.id}`}
                        color="primary"
                        size="small"
                        checked={!!itm.showCounter}
                        disabled
                      />
                    </Tooltip>
                    <Typography variant="body2" component="span">
                      Pre-Cnt
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Column 3: Default UOM selector */}
              <Grid item xs={12} md={5}>
                {itm.commodity?.name !== "Other" && (
                  <MuiTextField
                    select
                    name={`units_${itm.id}`}
                    label="Default UOM"
                    size="small"
                    variant="outlined"
                    required
                    fullWidth
                    disabled={disabled || itm.active !== "true"}
                    value={itm.units}
                  >
                    {getCommodityUnits(itm.commodity).map((unit) => (
                      <MenuItem
                        key={unit.id}
                        value={unit.id}
                      >{`${unit.name}: ${unit.description}`}</MenuItem>
                    ))}
                  </MuiTextField>
                )}
              </Grid>
            </Grid>
          ))}
      </Grid>

      {/* Equipment Types */}
      <Box mt={4}>
        <Grid container spacing={8}>
          <Grid item xs={12} md={6}>
            <Labeled label="RAIL EQUIPMENT TYPES" fullWidth>
              <ArrayField
                source="railEquipmentTypes.items"
                record={sortedRecord}
              >
                <Datagrid>
                  <TextField source="name" label="Description" />
                  <SelectField
                    source="arrTypeCode"
                    label="AAR Type Code"
                    choices={ARRCarTypeChoices}
                  />
                  <NumberField source="length" label="Length" />
                </Datagrid>
              </ArrayField>
            </Labeled>
          </Grid>

          <Grid item xs={12} md={6}>
            <Labeled label="TRUCK EQUIPMENT TYPES" fullWidth>
              <ArrayField
                source="truckEquipmentTypes.items"
                record={sortedRecord}
              >
                <Datagrid>
                  <TextField source="name" label="Description" />
                  <NumberField source="length" label="Length" />
                </Datagrid>
              </ArrayField>
            </Labeled>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
